// EditorPage — `EditorDrawer` with the `Drawer` it wraps taken out: the
// unsaved-changes host for a full-page editable surface that owns the whole
// main column and therefore has no chrome-owned dismiss affordance.
//
// @aiPurpose Architecturally enforce the unsaved-changes guard on full-page
//   editable surfaces by making `dirty` and `onConfirmClose` required props, so
//   a page editor registers with `UnsavedChangesProvider` exactly like a drawer
//   or modal.
// @aiInvariant Composes `useUnsavedChangesGuard`; does NOT duplicate it. Unlike
//   the two overlays there is no chrome-owned dismiss affordance to route: the
//   page itself owns its leave path and hands it over as `onConfirmClose`.
//   In-page destructive actions that unmount the editor without navigating go
//   through `useEditorGuard().confirmDestructive`.
import { useMemo, type ReactNode } from 'react';
import { useUnsavedChangesGuard } from './useUnsavedChangesGuard.js';
import { EditorGuardContext, type EditorGuardValue } from './useEditorGuard.js';

export interface EditorPageProps {
  /** True when the surface has meaningful unsaved changes vs its baseline. */
  dirty: boolean;
  /** True while a save/create/reset/delete/upload mutation owned by the surface is in flight. */
  busy?: boolean;
  /** The page's own leave path. Called when the user confirms discard or when the surface is clean. */
  onConfirmClose: () => void;
  children: ReactNode;
}

export function EditorPage({ dirty, busy = false, onConfirmClose, children }: EditorPageProps) {
  const { confirmDestructive, bypassNext } = useUnsavedChangesGuard({
    dirty,
    busy,
    onConfirmClose,
  });

  const guardValue = useMemo<EditorGuardValue>(
    () => ({ confirmDestructive, bypassNext }),
    [confirmDestructive, bypassNext],
  );

  return <EditorGuardContext.Provider value={guardValue}>{children}</EditorGuardContext.Provider>;
}
